import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BodyText, Div, Flex, Input } from '@gaiads/telia-react-component-library';

import { HTMLElementProps } from '../../../utils/types';
import { BaseEditableItemFieldProps } from '../EditableItem';
import EditableItemIcons, { EditableItemIconsProps } from '../EditableItemIcons/EditableItemIcons';

export interface EditableItemInputProps extends BaseEditableItemFieldProps, EditableItemIconsProps {
  value?: string;
  edit?: boolean;
  onSave?: (value?: string) => Promise<boolean> | boolean;
  onBlur?: () => void;
  field?: (content?: string) => JSX.Element;
  focusOnEdit?: boolean;
  inputProps?: Partial<React.ComponentProps<typeof Input> & HTMLElementProps>;
  flexProps?: Partial<React.ComponentProps<typeof Flex> & HTMLElementProps>;
  divProps?: Partial<React.ComponentProps<typeof Div> & HTMLElementProps>;
  iconFlexProps?: Partial<React.ComponentProps<typeof Flex> & HTMLElementProps>;
}

export default function EditableItemInput(props: EditableItemInputProps) {
  const {
    edit,
    setIsEditingItem,
    onSave,
    onBlur,
    onEditClick,
    onDelete,
    field,
    isReference,
    showSave,
    showDelete,
    focusOnEdit,
    flexProps,
    inputProps,
    iconFlexProps,
    editIconProps,
    deleteIconProps,
    divProps,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState(props.value);
  const [isEditingInput, setIsEditingInput] = useState(edit);

  /* blur conflicts with icon click until fixed in TRCL
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    console.log(event);
    onBlur?.();
    handleEditing(false);
    setValue(inputProps?.value);
  };*/

  const handleEditing = useCallback(
    (isEditing: boolean) => {
      setIsEditingInput(isEditing);
      setIsEditingItem?.(isEditing);
    },
    [setIsEditingItem],
  );

  const handleSave = async () => {
    const success = await onSave?.(value);
    if (success) {
      handleEditing(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      onBlur?.();
      handleEditing(false);
      setValue(props.value);
    } else if (event.key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    if (edit) {
      handleEditing(true);
    } else {
      handleEditing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit]);

  useEffect(() => {
    if (!isEditingInput && inputProps?.errorText) {
      handleEditing(true);
    }
  }, [inputProps?.errorText, isEditingInput, handleEditing]);

  useEffect(() => {
    if (isEditingInput && focusOnEdit) {
      inputRef.current?.focus();
    }
  }, [isEditingInput, focusOnEdit]);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const valueOrPlaceholder = value || `${inputProps?.placeholder}${inputProps?.required ? ' ' : ''}`;

  return (
    <Flex
      {...flexProps}
      className={[
        'editable-field__field',
        !value && 'editable-field__field--blank',
        isReference && 'editable-field__field--reference',
        flexProps?.className,
      ]}
    >
      <Div {...divProps} className={['editable-field__input-container', divProps?.className]}>
        {isEditingInput ? (
          <Input
            inputRef={inputRef}
            label=""
            className="editable-field__input"
            onChange={(event, value) => setValue(value)}
            onKeyDown={handleKeyPress}
            onIconClick={showSave ? handleSave : undefined}
            //onBlur={handleBlur}
            iconName={showSave ? 'checkmark' : undefined}
            value={value}
            {...inputProps}
          />
        ) : (
          <>
            <BodyText>{field?.(valueOrPlaceholder) || valueOrPlaceholder}</BodyText>
            {inputProps?.required && !value && <BodyText className="telia-react-input_required">*</BodyText>}
          </>
        )}
      </Div>
      {!isEditingInput && (
        <EditableItemIcons
          showDelete={showDelete}
          onEditClick={onEditClick}
          onDelete={onDelete}
          setIsEditing={handleEditing}
          flexProps={iconFlexProps}
          editIconProps={editIconProps}
          deleteIconProps={deleteIconProps}
        />
      )}
    </Flex>
  );
}
